import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut as fbSignOut,
} from 'firebase/auth';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  setDoc,
  updateDoc,
  where,
  limit as qLimit,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { deleteField } from 'firebase/firestore';
import { getBlob, getBytes, getStorage, ref as storageRef, uploadBytes } from 'firebase/storage';
import type {
  AllyTurnRequest,
  AllyTurnResponse,
  AstroSummary,
  ConnectionDoc,
  ConnectionIntent,
  ConversationDoc,
  CreditLedgerEntry,
  DiscoveryFeedResult,
  MediaRef,
  MemoryItem,
  MemorySuggestion,
  MessageDoc,
  RecommendationDoc,
  ReportReason,
  UserDoc,
} from '@astrally/shared';
import { getFirebase, type FirebaseBundle } from '../firebase';
import { localdb } from '../localdb';
import type {
  Backend,
  CompatibilityView,
  EntitlementSnapshot,
  FeedPage,
  FeedPost,
  FeedComment,
  FeedCommentsPage,
  FeedCommentReceipt,
  FeedLikeResult,
  PreparedUpload,
  SentMessage,
} from './types';

/** What `mediaGrantUpload` hands back. Mirrors MediaUploadGrant in functions. */
interface UploadGrant {
  path: string;
  contentType: string;
  cacheControl: string;
  maxBytes: number;
  moderation: { required: boolean; supported: boolean; reason?: string };
}

/**
 * Memories Ally has SUGGESTED and the user has not answered yet, held on the
 * device under this key until they do. Scoped by uid so switching accounts
 * cannot show one person another's pending suggestions.
 */
function pendingMemoriesKey(uid: string): string {
  return `pendingMemories:${uid}`;
}

/** Keeps a damaged or stale device list from filling the memory screen. */
const MAX_PENDING_MEMORIES = 20;

/**
 * FirebaseBackend — real Auth + Firestore + callable Functions.
 * Works against the Emulator Suite (VITE_USE_EMULATORS=true) or a real
 * project. All privileged transitions (intros, blocks, scores, deletion)
 * go through Functions; Firestore rules deny direct writes.
 */
export class FirebaseBackend implements Backend {
  readonly kind = 'firebase' as const;
  private fb!: FirebaseBundle;

  async init(): Promise<void> {
    this.fb = getFirebase();
    await new Promise<void>((resolve) => {
      const unsub = this.fb.auth.onAuthStateChanged(() => {
        unsub();
        resolve();
      });
    });
  }

  currentUid(): string | null {
    return this.fb.auth.currentUser?.uid ?? null;
  }

  async signIn(email: string, password: string): Promise<string> {
    const cred = await signInWithEmailAndPassword(this.fb.auth, email, password);
    return cred.user.uid;
  }

  async signUp(email: string, password: string): Promise<string> {
    const cred = await createUserWithEmailAndPassword(this.fb.auth, email, password);
    return cred.user.uid;
  }

  async signOut(): Promise<void> {
    await fbSignOut(this.fb.auth);
  }

  private req(): string {
    const uid = this.currentUid();
    if (!uid) throw new Error('Not signed in');
    return uid;
  }

  private call<TReq, TRes>(name: string) {
    return async (data: TReq): Promise<TRes> => {
      const fn = httpsCallable<TReq, TRes>(this.fb.fns, name);
      const res = await fn(data);
      return res.data;
    };
  }

  async getUser(uid: string): Promise<UserDoc | null> {
    try {
      const snap = await getDoc(doc(this.fb.db, 'users', uid));
      return snap.exists() ? (snap.data() as UserDoc) : null;
    } catch (e) {
      // "Not visible to you" and "does not exist" are the same thing to every
      // caller here — both mean there is no person to show, and every call site
      // already handles null (ProfileView shows its "no longer available"
      // card, the message list falls back to a placeholder name).
      //
      // The rules now hide accounts that never joined the social layer, so a
      // permission-denied on someone else's profile is an EXPECTED outcome
      // rather than a fault, and must not surface as an unhandled rejection.
      // Your own document is always readable, so this never masks a real
      // problem with the signed-in user's own profile.
      if ((e as { code?: string } | null)?.code === 'permission-denied') return null;
      throw e;
    }
  }

  async saveUser(user: UserDoc): Promise<void> {
    await setDoc(doc(this.fb.db, 'users', user.uid), user, { merge: true });
    await this.call<Record<string, never>, unknown>('scoresRecompute')({}).catch(() => undefined);
  }

  async getLearnProgress(): Promise<string[] | null> {
    const uid = this.currentUid();
    if (!uid) return null;
    const snap = await getDoc(doc(this.fb.db, `users/${uid}/private/learn`));
    const ids = snap.data()?.doneLessonIds;
    return Array.isArray(ids) ? ids.filter((x): x is string => typeof x === 'string') : [];
  }

  async saveLearnProgress(doneLessonIds: string[]): Promise<void> {
    const uid = this.currentUid();
    if (!uid) return;
    await setDoc(
      doc(this.fb.db, `users/${uid}/private/learn`),
      { uid, doneLessonIds, updatedAtISO: new Date().toISOString() },
      { merge: true },
    );
  }

  async saveAstroSummary(summary: AstroSummary): Promise<void> {
    const uid = this.req();
    await setDoc(
      doc(this.fb.db, `users/${uid}/private/profile`),
      { uid, astroSummary: summary, timeConfidence: summary.timeConfidence },
      { merge: true },
    );
    // Refresh the server-side discovery index so the new chart features are
    // used for matching (also recomputes profile completeness).
    await this.call<Record<string, never>, unknown>('scoresRecompute')({}).catch(() => undefined);
  }

  async pauseRecommendations(paused: boolean): Promise<void> {
    const uid = this.req();
    // `discoverable` is server-controlled (rules deny client writes), so a
    // pause is applied through the social callable.
    await updateDoc(doc(this.fb.db, 'users', uid), { recommendationsPaused: paused });
    await this.call<Record<string, unknown>, unknown>('socialSetEnabled')({
      enabled: !paused,
    }).catch(() => undefined);
  }

  async setSocialEnabled(enabled: boolean, intents?: ConnectionIntent[]): Promise<void> {
    await this.call<Record<string, unknown>, unknown>('socialSetEnabled')({ enabled, intents });
  }

  async allyTurn(request: AllyTurnRequest): Promise<AllyTurnResponse> {
    // Drop undefined keys: the callable protocol would send them as null,
    // which server-side validation treats as a wrong type.
    const clean = JSON.parse(JSON.stringify(request)) as AllyTurnRequest;
    const response = await this.call<AllyTurnRequest, AllyTurnResponse>('aiChat')(clean);
    // Suggestions land on the DEVICE, not in Firestore. The server used to
    // write them straight into memories/{uid}/items — sensitive ones included —
    // which made "Sensitive — stored only if you approve" untrue. Staging them
    // here means approving is the first moment anything reaches the cloud.
    // Best-effort: losing a suggestion is a far smaller failure than losing the
    // reply the user just paid for.
    await this.stageSuggestions(response.memorySuggestions ?? []).catch(() => undefined);
    return response;
  }

  /** Suggestions awaiting the user's decision — device-only, never uploaded. */
  private async readPending(uid: string): Promise<MemoryItem[]> {
    const raw = await localdb.get<unknown>('kv', pendingMemoriesKey(uid)).catch(() => undefined);
    if (!Array.isArray(raw)) return [];
    return (raw as MemoryItem[]).filter(
      (m): m is MemoryItem =>
        !!m && typeof m.id === 'string' && typeof m.content === 'string' && m.content.length > 0,
    );
  }

  private async stageSuggestions(suggestions: MemorySuggestion[]): Promise<void> {
    const uid = this.currentUid();
    if (!uid || suggestions.length === 0) return;
    const existing = await this.readPending(uid);
    const seen = new Set(existing.map((m) => m.content.trim().toLowerCase()));
    const now = new Date().toISOString();
    const added: MemoryItem[] = [];
    for (const s of suggestions.slice(0, 5)) {
      // 500 is the ceiling the Firestore rules enforce on create; clamping now
      // means a confirmed memory can never be rejected at the moment the user
      // approves it.
      const content = s.content.trim().slice(0, 500);
      const key = content.toLowerCase();
      if (!content || seen.has(key)) continue;
      seen.add(key);
      added.push({
        id: `pending-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
        category: s.category,
        content,
        status: 'suggested',
        source: s.confidence >= 0.85 ? 'user-stated' : 'inferred',
        confidence: s.confidence,
        sensitive: s.sensitive,
        cloudApproved: false,
        createdAtISO: now,
        updatedAtISO: now,
      });
    }
    if (added.length === 0) return;
    await localdb.set(
      'kv',
      pendingMemoriesKey(uid),
      [...existing, ...added].slice(-MAX_PENDING_MEMORIES),
    );
  }

  async listMemories(): Promise<MemoryItem[]> {
    const uid = this.req();
    const snap = await getDocs(
      query(collection(this.fb.db, `memories/${uid}/items`), orderBy('createdAtISO', 'desc')),
    );
    const stored = snap.docs.map((d) => d.data() as MemoryItem);
    // Pending suggestions are merged in so the memory screen still shows a
    // "Waiting for your approval" section — they simply do not exist in the
    // cloud yet.
    return [...(await this.readPending(uid)), ...stored];
  }

  async addMemory(item: MemoryItem): Promise<void> {
    const uid = this.req();
    await setDoc(doc(this.fb.db, `memories/${uid}/items/${item.id}`), item);
  }

  async resolveMemory(
    memoryId: string,
    action: 'confirm' | 'reject' | 'delete' | 'edit',
    content?: string,
    cloudApproved?: boolean,
  ): Promise<void> {
    const uid = this.req();
    const pending = await this.readPending(uid);
    const item = pending.find((m) => m.id === memoryId);
    if (item) {
      if (action === 'confirm' || action === 'edit') {
        const text = action === 'edit' ? (content ?? '').trim().slice(0, 500) : item.content;
        if (!text) throw new Error('There is nothing to save — write the memory first.');
        // The FIRST time this suggestion touches the cloud, and only because
        // the user just said yes. Written before the device copy is dropped so
        // a failed write leaves the suggestion where the user can retry it.
        await this.addMemory({
          ...item,
          content: text,
          status: 'confirmed',
          cloudApproved: cloudApproved === true,
          updatedAtISO: new Date().toISOString(),
        });
      }
      // reject/delete need no server call at all: nothing was ever uploaded, so
      // forgetting the device copy IS the deletion.
      await localdb.set(
        'kv',
        pendingMemoriesKey(uid),
        pending.filter((m) => m.id !== memoryId),
      );
      return;
    }
    await this.call<Record<string, unknown>, unknown>('memoryResolve')({
      memoryId,
      action,
      content,
      cloudApproved,
    });
  }

  async generateRecommendations(): Promise<number> {
    const res = await this.call<Record<string, never>, { created: number }>('recoGenerate')({});
    return res.created;
  }

  async listRecommendations(): Promise<RecommendationDoc[]> {
    const uid = this.req();
    const snap = await getDocs(
      query(
        collection(this.fb.db, `recommendations/${uid}/items`),
        orderBy('createdAtISO', 'desc'),
        qLimit(30),
      ),
    );
    return snap.docs.map((d) => d.data() as RecommendationDoc);
  }

  async setRecommendationStatus(
    recId: string,
    status: 'seen' | 'dismissed' | 'not-similar',
  ): Promise<void> {
    const uid = this.req();
    await updateDoc(doc(this.fb.db, `recommendations/${uid}/items/${recId}`), {
      status,
      updatedAtISO: new Date().toISOString(),
    });
  }

  // --- commerce -------------------------------------------------------------

  async getEntitlement(): Promise<EntitlementSnapshot> {
    return this.call<Record<string, never>, EntitlementSnapshot>('entitlementCheck')({});
  }

  async purchase(
    productId: string,
    platform: 'play' | 'mock' | 'dev' = 'mock',
  ): Promise<{ granted: string }> {
    // [MOCKED BILLING] 'mock' until the Play Billing plugin lands (it will
    // supply a purchaseToken and switch this to 'play'). 'dev' is the
    // allowlisted developer unlock for on-device testing.
    return this.call<Record<string, unknown>, { granted: string }>('purchaseVerify')({
      platform,
      productId,
    });
  }

  async listLedger(): Promise<CreditLedgerEntry[]> {
    const uid = this.req();
    const snap = await getDocs(
      query(
        collection(this.fb.db, 'creditLedger'),
        where('uid', '==', uid),
        orderBy('createdAtISO', 'desc'),
        qLimit(50),
      ),
    );
    return snap.docs.map((d) => d.data() as CreditLedgerEntry);
  }

  // --- discovery + connections ----------------------------------------------

  async discoveryFeed(): Promise<DiscoveryFeedResult> {
    return this.call<Record<string, never>, DiscoveryFeedResult>('discoveryFeed')({});
  }

  async discoveryAction(targetUid: string, action: 'pass' | 'save' | 'unsave'): Promise<void> {
    await this.call<Record<string, unknown>, unknown>('discoveryAction')({ targetUid, action });
  }

  async compatibilityGet(targetUid: string): Promise<CompatibilityView> {
    return this.call<Record<string, unknown>, CompatibilityView>('compatibilityGet')({ targetUid });
  }

  async connectionRequest(
    recipientUid: string,
    introMessage?: string,
  ): Promise<{ connectionId: string; quota: { remaining: number; kind: string } }> {
    return this.call<
      Record<string, unknown>,
      { connectionId: string; quota: { remaining: number; kind: string } }
    >('connectionRequest')({ recipientUid, introMessage });
  }

  async connectionRespond(
    connectionId: string,
    accept: boolean,
  ): Promise<{ status: string; conversationId?: string }> {
    return this.call<Record<string, unknown>, { status: string; conversationId?: string }>(
      'connectionRespond',
    )({ connectionId, accept });
  }

  async connectionWithdraw(connectionId: string): Promise<void> {
    await this.call<Record<string, unknown>, unknown>('connectionWithdraw')({ connectionId });
  }

  async listConnections(): Promise<{ incoming: ConnectionDoc[]; outgoing: ConnectionDoc[] }> {
    const uid = this.req();
    const [inSnap, outSnap] = await Promise.all([
      getDocs(
        query(
          collection(this.fb.db, 'connections'),
          where('recipientUid', '==', uid),
          where('status', '==', 'pending'),
        ),
      ),
      getDocs(query(collection(this.fb.db, 'connections'), where('requesterUid', '==', uid))),
    ]);
    return {
      incoming: inSnap.docs.map((d) => d.data() as ConnectionDoc),
      outgoing: outSnap.docs.map((d) => d.data() as ConnectionDoc),
    };
  }

  async listConversations(): Promise<ConversationDoc[]> {
    const uid = this.req();
    const snap = await getDocs(
      query(collection(this.fb.db, 'conversations'), where('members', 'array-contains', uid)),
    );
    return snap.docs
      .map((d) => d.data() as ConversationDoc)
      .sort((a, b) =>
        (b.lastMessageAtISO ?? b.createdAtISO).localeCompare(a.lastMessageAtISO ?? a.createdAtISO),
      );
  }

  async listMessages(conversationId: string): Promise<MessageDoc[]> {
    const snap = await getDocs(
      query(
        collection(this.fb.db, `conversations/${conversationId}/messages`),
        orderBy('sentAtISO', 'asc'),
        qLimit(200),
      ),
    );
    return snap.docs.map((d) => d.data() as MessageDoc);
  }

  /**
   * Send goes through the `messageSend` callable, NOT a direct Firestore
   * write, because automated moderation has to run before the text exists.
   * ConversationView polls every 4 seconds, so anything that inspects a
   * message after it lands has already lost the race with the recipient's
   * next poll.
   *
   * A refusal arrives as a callable error carrying `details.reason` — see
   * moderationRefusal() in ./types. The composer keeps the draft in that case
   * (ConversationView.send) so nobody has to retype what they wrote.
   *
   * The other message operations (reactions, edits, receipts, delete-for-self)
   * deliberately keep their direct-write paths; only creation is gated.
   */
  async sendMessage(
    conversationId: string,
    text: string,
    replyTo?: string,
    media?: MediaRef,
  ): Promise<SentMessage> {
    this.req();
    // The result is not decoration. It carries `media.recipientCanView`, which
    // is the only way the sender can learn that their photo landed as a locked
    // blur — a client cannot read the other member's entitlement document, and
    // a send that succeeds while the other person sees nothing is the failure
    // this whole feature exists to prevent.
    return this.call<
      { conversationId: string; text: string; replyTo?: string; media?: MediaRef },
      SentMessage
    >('messageSend')({
      conversationId,
      text,
      ...(replyTo ? { replyTo } : {}),
      ...(media ? { media } : {}),
    });
  }

  async reactToMessage(conversationId: string, messageId: string, emoji: string | null): Promise<void> {
    const uid = this.req();
    await updateDoc(doc(this.fb.db, `conversations/${conversationId}/messages/${messageId}`), {
      [`reactions.${uid}`]: emoji ?? deleteField(),
    });
  }

  async editMessage(conversationId: string, messageId: string, text: string): Promise<void> {
    await updateDoc(doc(this.fb.db, `conversations/${conversationId}/messages/${messageId}`), {
      text,
      editedAtISO: new Date().toISOString(),
    });
  }

  async deleteMessageForSelf(conversationId: string, messageId: string): Promise<void> {
    const uid = this.req();
    const ref = doc(this.fb.db, `conversations/${conversationId}/messages/${messageId}`);
    const snap = await getDoc(ref);
    const current = (snap.data()?.deletedFor as string[] | undefined) ?? [];
    await updateDoc(ref, { deletedFor: [...new Set([...current, uid])] });
  }

  async endConversation(conversationId: string): Promise<void> {
    // Ending = blocking further messages without a block record; server marks it.
    await this.call<Record<string, unknown>, unknown>('userBlock').call(this, {
      targetUid: await this.otherMember(conversationId),
    });
  }

  private async otherMember(conversationId: string): Promise<string> {
    const uid = this.req();
    const snap = await getDoc(doc(this.fb.db, 'conversations', conversationId));
    const members = (snap.data()?.members as string[]) ?? [];
    return members.find((m) => m !== uid) ?? '';
  }

  // --- the members-only feed ------------------------------------------------

  async listFeed(opts?: { cursor?: string; limit?: number }): Promise<FeedPage> {
    return this.call<Record<string, unknown>, FeedPage>('feedList')({
      cursor: opts?.cursor,
      limit: opts?.limit,
    });
  }

  /**
   * Publish through the callable, never a direct Firestore write, for the same
   * reason messages go through `messageSend`: moderation has to reach a
   * verdict before the text exists. `firestore.rules` denies client creates on
   * `feedPosts`, so this is the only path that works.
   */
  async createPost(text: string, media?: MediaRef): Promise<FeedPost> {
    this.req();
    return this.call<Record<string, unknown>, FeedPost>('feedPost')({
      text,
      ...(media ? { media } : {}),
    });
  }

  async deletePost(postId: string): Promise<void> {
    await this.call<Record<string, unknown>, unknown>('feedPostDelete')({ postId });
  }

  async reportPost(postId: string, reason: ReportReason, context?: string): Promise<void> {
    await this.call<Record<string, unknown>, unknown>('feedPostReport')({
      postId,
      reason,
      context,
    });
  }

  // --- feed engagement --------------------------------------------------------

  /**
   * All four go through callables for the same reason posts do, and
   * firestore.rules denies the direct writes that would otherwise tempt a
   * shortcut: `likeCount` and `commentCount` live on the parent post and are
   * only correct if they move in the same transaction as the like or comment
   * document, and a comment has to clear moderation before it exists at all.
   */
  async toggleLike(postId: string, on: boolean): Promise<FeedLikeResult> {
    return this.call<Record<string, unknown>, FeedLikeResult>('feedPostLike')({ postId, on });
  }

  async addComment(postId: string, text: string): Promise<FeedCommentReceipt> {
    return this.call<Record<string, unknown>, FeedCommentReceipt>('feedPostComment')({
      postId,
      text,
    });
  }

  async deleteComment(postId: string, commentId: string): Promise<void> {
    await this.call<Record<string, unknown>, unknown>('feedCommentDelete')({ postId, commentId });
  }

  async listComments(postId: string, cursor?: string): Promise<FeedCommentsPage> {
    return this.call<Record<string, unknown>, FeedCommentsPage>('feedCommentsList')({
      postId,
      cursor,
    });
  }

  /**
   * The four-step upload the media contract requires, in order:
   * grant → upload (WITH the returned Cache-Control) → moderate → return.
   *
   * The Cache-Control is not optional and not cosmetic. Storage rules cannot
   * inspect it, so forgetting it fails silently and permanently: every repeat
   * view of that object then costs egress forever, on an object that is
   * immutable by rule and could have been cached for a year.
   *
   * Moderation is awaited rather than fired and forgotten. Its failure is the
   * post's failure — an image nobody approved must not reach a feed, and the
   * callable deletes the object when it refuses one, so a rejected upload
   * leaves nothing behind.
   */
  async uploadPostMedia(prepared: PreparedUpload): Promise<MediaRef> {
    this.req();
    const grant = await this.call<Record<string, unknown>, UploadGrant>('mediaGrantUpload')({
      target: 'media',
      kind: prepared.kind,
      contentType: prepared.contentType,
      bytes: prepared.bytes,
      durationMs: prepared.durationMs,
    });

    const storage = getStorage(this.fb.app);
    await uploadBytes(storageRef(storage, grant.path), prepared.blob, {
      contentType: grant.contentType,
      cacheControl: grant.cacheControl,
    });

    // Videos come back `checked: false` — nothing inspects video. The call is
    // still made: it records the honest `unchecked` receipt that feedPost
    // requires before it will let any path be referenced.
    await this.call<Record<string, unknown>, unknown>('mediaModerateImage')({ path: grant.path });

    return {
      path: grant.path,
      kind: prepared.kind,
      width: prepared.width,
      height: prepared.height,
      blurhash: prepared.blurhash,
      bytes: prepared.bytes,
      ...(prepared.durationMs ? { durationMs: prepared.durationMs } : {}),
    };
  }

  /**
   * Rule-checked fetch by object path.
   *
   * `getBlob` first, `getBytes` as a fallback: both are rule-checked XHR
   * requests, but some WebView builds reject the Blob response type. Neither
   * is `getDownloadURL`, and that is the point — a download URL carries a
   * token that bypasses Storage rules entirely, so it can never appear on a
   * document that non-Pro clients read.
   *
   * A non-Pro caller gets a permission error here. That is the paywall doing
   * its job, and it is why non-Pro viewers never call this: LockedMedia draws
   * the blur from the string on the post instead.
   */
  async loadMedia(path: string): Promise<Blob> {
    const fileRef = storageRef(getStorage(this.fb.app), path);
    try {
      return await getBlob(fileRef);
    } catch (e) {
      if ((e as { code?: string } | null)?.code === 'storage/unauthorized') throw e;
      const bytes = await getBytes(fileRef);
      return new Blob([bytes]);
    }
  }

  async blockUser(targetUid: string): Promise<void> {
    await this.call<Record<string, unknown>, unknown>('userBlock')({ targetUid });
  }

  async reportUser(targetUid: string, reason: ReportReason, context?: string): Promise<void> {
    await this.call<Record<string, unknown>, unknown>('userReport')({ targetUid, reason, context });
  }

  /**
   * Delete the account — in the cloud AND on this device.
   *
   * The device half is not optional. Firestore never holds exact birth data;
   * `birthProfile` (raw birth date, exact time, latitude/longitude),
   * `chart:{uid}` and every `allyChat:{uid}:*` transcript live only in
   * IndexedDB. Deleting only the server side left all of it sitting on the
   * phone — and with android:allowBackup="true" it stays eligible for Android
   * backup, so "still on the device" can quietly mean "also in someone's cloud
   * backup". That directly contradicts the promise the whole product rests on.
   *
   * ORDER IS DELIBERATE: server first, device second.
   *  - Wiping first would be the worse failure mode. If accountDelete is then
   *    rejected or unreachable, the user is left with a live account and no
   *    birth details, no chart and no conversations, with nothing anywhere to
   *    restore them from — an irreversible loss caused by a retryable error.
   *  - Once accountDelete RESOLVES the account is gone, so from that point the
   *    on-device copy is orphaned personal data and must go no matter what
   *    happens next. That is why the wipe sits in `finally` around signOut
   *    rather than after it: a sign-out that throws (offline, token already
   *    revoked by the deleted auth user) must not be able to leave birth data
   *    behind.
   */
  async deleteAccount(): Promise<void> {
    await this.call<Record<string, never>, unknown>('accountDelete')({});
    try {
      await this.signOut();
    } finally {
      await localdb.wipeEverything();
    }
  }

  async exportData(): Promise<Record<string, unknown>> {
    const uid = this.req();
    return {
      user: await this.getUser(uid),
      memories: await this.listMemories(),
      recommendations: await this.listRecommendations(),
      conversations: await this.listConversations(),
      note: 'Cloud export. Exact birth data stays on-device and is not part of cloud storage.',
    };
  }
}
